import { useRouter } from '@abyss/web/hooks/useRouter';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Modal } from '@abyss/web/ui/Modal/Modal';
import { SelectInput } from '@abyss/web/ui/SelectInput';
import { t } from 'i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import find from 'lodash/find';
import { useEffect, useRef, useState } from 'react';

import {
  Constants,
  NUMBER_OF_PDF_PRACTITIONER_RESULTS,
} from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { LocationSearchDesktop } from '../../common/PSXHeader/LocationSearchDesktop/LocationSearchDesktop';
import { useGeoLocationStorage } from '../../hooks/useGeoLocationStorage';
import { useLagoon } from '../../hooks/useLagoon';
import { usePrintPdfProviderResults } from '../../hooks/useProviderSearch/useProviderSearchV2';
import { useConfig } from '../ProviderSearch/context/Analytics';
import { SendEmailForProviderList } from '../ProviderSearch/routes/MapView/ShareDrawer/ShareDrawerContents/SendEmailForProviderList';
import { getGuestPlanSelectionData } from './Guest.utils';
import {
  COVERAGE_LABELS,
  DIRECTORY_TYPE_OPTIONS,
  DirectoryTypeOption,
  PROVIDER_FILTER_OPTIONS,
  PrintDirectoryLagoonConfig,
  ProviderFilterOption,
  RADIUS_OPTIONS,
  RadiusOption,
  STEP_COUNT,
  STEP_SUBTITLES,
  STEP_TITLES,
} from './PrintDirectoryPage.constants';
import {
  ButtonGroup,
  ContentBlock,
  HeaderCloseButton,
  HeaderMetaBlock,
  HeaderMetaLabel,
  HeaderMetaValue,
  HeaderTitle,
  IconButtonInner,
  LocationFormContainer,
  PageContentArea,
  PageHeader,
  PageWrapper,
  PrimaryActionButton,
  ProgressBarContainer,
  ProgressSegment,
  RadioCircle,
  RadioDescription,
  RadioDot,
  RadioGroupContainer,
  RadioOptionButton,
  RadioTextWrapper,
  RadioTitle,
  ReviewSummaryLabel,
  ReviewSummaryList,
  ReviewSummaryRow,
  ReviewSummaryValue,
  ReviewWarningText,
  SLIDE_KEYFRAMES,
  SecondaryActionButton,
  StepContentWrapper,
  StepSubtitle,
  StepTitle,
  StyledDivider,
} from './PrintDirectoryPage.style';
import { PrintPdfGenerator, usePrintPdf } from './PrintPdfGenerator';
import { getBlueGreenGQLApiUrl } from './utils';

type DirectoryType = 'people' | 'places' | '';
type ProviderFilter = 'all' | 'premium' | '';
type SelectedLocation = {
  label: string;
  latitude?: string;
  longitude?: string;
  zipCode?: string;
  stateCode?: string;
};

const DIRECTORY_TYPE_TEST_IDS: Record<Exclude<DirectoryType, ''>, string> = {
  people: 'print-directory-type-people',
  places: 'print-directory-type-places',
};

const PROVIDER_FILTER_TEST_IDS: Record<Exclude<ProviderFilter, ''>, string> = {
  all: 'print-directory-filter-all',
  premium: 'print-directory-filter-premium',
};

type RadioOptionProps = {
  value: string;
  title: string;
  description?: string;
  selected: boolean;
  onSelect: (v: string) => void;
  testId?: string;
};

const RadioOption = ({
  value,
  title,
  description,
  selected,
  onSelect,
  testId,
}: RadioOptionProps) => (
  <RadioOptionButton
    aria-checked={selected}
    data-testid={testId}
    onClick={() => onSelect(value)}
    role="radio"
    type="button"
  >
    <RadioCircle>{selected && <RadioDot />}</RadioCircle>
    <RadioTextWrapper>
      <RadioTitle>{title}</RadioTitle>
      {description && <RadioDescription>{description}</RadioDescription>}
    </RadioTextWrapper>
  </RadioOptionButton>
);

const safeParsePrintDirectoryConfig = (
  message?: string
): PrintDirectoryLagoonConfig => {
  if (!message) return {};

  try {
    return JSON.parse(message) as PrintDirectoryLagoonConfig;
  } catch {
    return {};
  }
};

const getDirectoryTypeOptions = (
  lagoonOptions?: DirectoryTypeOption[]
): Array<DirectoryTypeOption & { value: string; testId: string }> =>
  DIRECTORY_TYPE_OPTIONS.map((defaultOption, index) => {
    const lagoonOption = lagoonOptions?.[index] as
      | (DirectoryTypeOption & { value?: string })
      | undefined;
    const value = (lagoonOption?.dnt_value ??
      lagoonOption?.value ??
      defaultOption.dnt_value) as DirectoryTypeOption['dnt_value'];

    return {
      value,
      dnt_value: value,
      title: lagoonOption?.title || defaultOption.title,
      description: lagoonOption?.description || defaultOption.description,
      testId: DIRECTORY_TYPE_TEST_IDS[value],
    };
  });

const getProviderFilterOptions = (
  lagoonOptions?: ProviderFilterOption[]
): Array<ProviderFilterOption & { value: string; testId: string }> =>
  PROVIDER_FILTER_OPTIONS.map((defaultOption, index) => {
    const lagoonOption = lagoonOptions?.[index] as
      | (ProviderFilterOption & { value?: string })
      | undefined;
    const value = (lagoonOption?.dnt_value ??
      lagoonOption?.value ??
      defaultOption.dnt_value) as ProviderFilterOption['dnt_value'];

    return {
      value,
      dnt_value: value,
      title: lagoonOption?.title || defaultOption.title,
      description: lagoonOption?.description || defaultOption.description,
      testId: PROVIDER_FILTER_TEST_IDS[value],
    };
  });

const getRadiusOptions = (
  lagoonOptions?: RadiusOption[]
): Array<RadiusOption & { value: string }> =>
  RADIUS_OPTIONS.map((defaultOption, index) => {
    const lagoonOption = lagoonOptions?.[index] as
      | (RadiusOption & { value?: string })
      | undefined;
    const value = (lagoonOption?.dnt_value ??
      lagoonOption?.value ??
      defaultOption.dnt_value) as RadiusOption['dnt_value'];

    return {
      value,
      dnt_value: value,
      label: lagoonOption?.label || defaultOption.label,
    };
  });

export const PrintDirectoryPage = () => {
  const { navigate, getSearchParams } = useRouter();
  const flags = useFlags();
  const [searchParams] = getSearchParams();
  const guestBGApiUrl = getBlueGreenGQLApiUrl(flags, searchParams);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [directoryType, setDirectoryType] = useState<DirectoryType>('');
  const [providerFilter, setProviderFilter] = useState<ProviderFilter>('');
  const [location, setLocation] = useState('');
  const [selectedLocation, setSelectedLocation] =
    useState<SelectedLocation | null>(null);
  const [radius, setRadius] = useState('');
  const [animKey, setAnimKey] = useState(0);
  const [openSendEmailDrawer, setOpenSendEmailDrawer] = useState(false);
  const [isEmailSubmitted, setIsEmailSubmitted] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'right' | 'left'>(
    'right'
  );
  const pageRef = useRef<HTMLDivElement>(null);
  const clientName = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.CLIENT_NAME
  );

  const lagoonMessagingTable = useLagoon('ui-messaging');
  const lagoonUIMessage =
    typeof lagoonMessagingTable === 'function'
      ? lagoonMessagingTable()
      : lagoonMessagingTable;
  const { selectedPlan = {}, coverageTypeCode: coverageType = '' } =
    getGuestPlanSelectionData();
  const planName = selectedPlan?.planName || '';
  const [, getProviderResults] = usePrintPdfProviderResults({
    url: guestBGApiUrl,
  });
  const {
    zipcode,
    longitude: geoLongitude,
    latitude: geoLatitude,
  } = useGeoLocationStorage();
  const emailKey =
    useConfig('ENABLE_PRINT_PDF_EMAIL')?.trim()?.toLowerCase() === 'true';

  const isDirectoryPrint =
    new URLSearchParams(window.location.search).get('isDirectoryPrint') ===
    'true';

  // Extract coordinates from stored guest location or use geolocation
  const getCoordinatesFromLocation = () => {
    if (selectedLocation?.latitude && selectedLocation?.longitude) {
      return {
        lat: parseFloat(selectedLocation.latitude),
        lon: parseFloat(selectedLocation.longitude),
      };
    }

    const saved = sessionStorage.getItem(
      Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION
    );

    if (!saved) {
      return { lat: geoLatitude, lon: geoLongitude };
    }

    const parsed = JSON.parse(saved);
    if (parsed?.latitude && parsed?.longitude) {
      return {
        lat: parseFloat(parsed.latitude),
        lon: parseFloat(parsed.longitude),
      };
    }

    return { lat: geoLatitude, lon: geoLongitude };
  };
  useEffect(() => {
    const saved = sessionStorage.getItem(
      Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION
    );
    const parsed = saved ? JSON.parse(saved) : null;
    const locationLabel = parsed?.label || zipcode;

    if (locationLabel) {
      setLocation(locationLabel);
      setSelectedLocation(
        parsed
          ? {
              label: parsed.label,
              latitude: parsed.latitude,
              longitude: parsed.longitude,
              zipCode: parsed.zipcode,
              stateCode: parsed.stateCode,
            }
          : { label: locationLabel }
      );
    }
  }, [zipcode]);

  const msg = (key: string) =>
    find(lagoonUIMessage, { key })?.message || undefined;

  const lagoonConfig = safeParsePrintDirectoryConfig(
    msg(ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_CONFIG)
  );
  const directoryTypeOptions = getDirectoryTypeOptions(
    lagoonConfig?.directoryTypeOptions
  );
  const providerFilterOptions = getProviderFilterOptions(
    lagoonConfig?.providerFilterOptions
  );
  const radiusOptions = getRadiusOptions(lagoonConfig?.radiusOptions);
  const stepTitles = lagoonConfig?.stepTitles || STEP_TITLES;
  const stepSubtitles = lagoonConfig?.stepSubtitles || STEP_SUBTITLES;

  const getStepHeading = () => {
    if (step === 1)
      return (
        msg(
          ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_MAIN_HEADING
        ) || stepTitles[1]
      );
    if (step === 2)
      return (
        msg(ConstantsLagoon.GUEST_PLAN_SELECTION.HEADING_PEOPLE_DIRECTORY) ||
        stepTitles[2]
      );
    return directoryType === 'places'
      ? msg(ConstantsLagoon.GUEST_PLAN_SELECTION.HEADING_PLACE_DIRECTORY) ||
          stepTitles[3]
      : msg(ConstantsLagoon.GUEST_PLAN_SELECTION.HEADING_PEOPLE_DIRECTORY) ||
          stepTitles[3];
  };

  const getStepSubHeading = () => {
    if (step === 1)
      return (
        msg(
          ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_MAIN_SUB_HEADING
        ) || stepSubtitles[1]
      );
    if (step === 2)
      return (
        msg(
          ConstantsLagoon.GUEST_PLAN_SELECTION
            .PRINT_DIRECTORY_SECOND_SUB_HEADING
        ) || stepSubtitles[2]
      );
    if (step === 3)
      return (
        msg(
          ConstantsLagoon.GUEST_PLAN_SELECTION
            .PRINT_DIRECTORY_RADIUS_PAGE_SUB_HEADING
        ) || stepSubtitles[3]
      );
    return stepSubtitles[step];
  };

  const goToStep = (next: 1 | 2 | 3 | 4, dir: 'right' | 'left') => {
    setSlideDirection(dir);
    setAnimKey((k) => k + 1);
    setStep(next);
  };

  const { openPrintWindow, printProviderPdf, showPrintError } = usePrintPdf();

  const handleBack = () => {
    if (isEmailSubmitted) {
      navigate(-1);
      return;
    }
    if (step === 2) goToStep(1, 'left');
    else if (step === 3) {
      if (directoryType === 'places') goToStep(1, 'left');
      else goToStep(2, 'left');
    } else if (step === 4) goToStep(3, 'left');
  };

  const handlePrint = async () => {
    setIsPdfGenerating(true);

    const providerTypeMap: Record<string, string> = {
      people: 'practitioner',
      places: 'organization',
    };
    const providerType = providerTypeMap[directoryType] || 'practitioner';
    const premiumCarePhysician = getResolvedProviderFilter() === 'premium';
    const searchRadiusNum = Math.max(parseInt(radius, 10) || 50, 1);

    try {
      const { lat, lon } = getCoordinatesFromLocation();
      const result = await getProviderResults({
        providerType,
        premiumCarePhysician,
        latitude: String(lat).trim(),
        longitude: String(lon).trim(),
        searchRadius: searchRadiusNum,
        planName,
        pageSize: NUMBER_OF_PDF_PRACTITIONER_RESULTS,
        stateCode: selectedLocation?.stateCode,
      });
      const providers = result?.data?.printPdfProviderSearch?.providers || [];

      const printWindow = openPrintWindow(pdfTitle);

      printProviderPdf(printWindow, providers, location, radius, {
        pdfTitle,
        directoryName: getDirectoryTypeLabel(),
        planName: selectedPlan?.planName || '',
        clientName: clientName || '',
        dayAbbreviations: {
          sunday: t('sunday'),
          monday: t('monday'),
          tuesday: t('tuesday'),
          wednesday: t('wednesday'),
          thursday: t('thursday'),
          friday: t('friday'),
          saturday: t('saturday'),
        },
        phoneLabel: t('SHARE_PDF.Phone'),
        faxLabel: t('SHARE_PDF.Fax'),
        emergencyLabel: t('After hours') || 'Emergency',
        closedLabel: t('Closed') || 'Closed',
        providerGroupLabel: t('PROVIDER_GROUP') || 'Provider Group',
        hospitalAffiliationsLabel:
          t('HOSPITAL_AFFILIATIONS') || 'Hospital Affiliations',
        accessibilityLabel:
          t('PROVIDER_LOCATIONS_DETAILS.ACCESSIBILITY') || 'Accessibility',
        medicalStaffLabel: 'Medical Staff',
        acceptingNewPatientsLabel:
          t('Accepting new patients') || 'Accepting new patients',
        virtualVisitOnlyLabel: 'Virtual Visit Only',
        ttyLabel: t('TTY') || 'TTY',
        licenseLabel: t('SHARE_PDF.License No') || 'License',
        npiLabel: 'NPI',
        boardCertificationsLabel:
          t('Board Certifications') || 'Board Certifications',
        referralNeededLabel: 'Referral Needed',
        resultEntityLabel:
          directoryType === 'places' ? 'Facilities' : 'Providers',
        content: lagoonConfig?.pdfContent,
      });
    } catch (error) {
      const printWindow = openPrintWindow(pdfTitle);
      showPrintError(
        printWindow,
        error instanceof Error ? error.message : 'Unknown print error'
      );
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handleSendEmail = async () => {
    setOpenSendEmailDrawer(false);
    setIsEmailSubmitted(true);
    setTimeout(() => {
      pageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const isContinueDisabled = (): boolean => {
    if (step === 1) return !directoryType;
    if (step === 2) return !providerFilter;
    if (step === 3) return !location || !radius;
    return false;
  };

  const handleContinue = () => {
    if (step === 1 && directoryType) {
      if (directoryType === 'places') goToStep(3, 'right');
      else goToStep(2, 'right');
    } else if (step === 2 && providerFilter) goToStep(3, 'right');
    else if (step === 3 && location && radius) goToStep(4, 'right');
    else if (step === 4) handlePrint();
  };

  const renderRadioGroup = (
    options: ReadonlyArray<{
      value: string;
      title: string;
      description: string;
      testId: string;
    }>,
    selectedValue: string,
    onChange: (v: string) => void,
    ariaLabel: string,
    testId: string
  ) => (
    <RadioGroupContainer
      aria-label={ariaLabel}
      data-testid={testId}
      role="radiogroup"
    >
      {options.map((opt) => (
        <div key={opt.value}>
          <StyledDivider />
          <RadioOption
            description={opt.description}
            onSelect={onChange}
            selected={selectedValue === opt.value}
            testId={opt.testId}
            title={opt.title}
            value={opt.value}
          />
        </div>
      ))}
      <StyledDivider />
    </RadioGroupContainer>
  );

  const getDirectoryTypeLabel = (): string => {
    const match = directoryTypeOptions.find(
      (opt) => opt.dnt_value === directoryType
    );
    return match ? match.title : '';
  };

  const getResolvedProviderFilter = (): ProviderFilter =>
    directoryType === 'places' ? 'all' : providerFilter;

  const getFilterLabel = (): string => {
    const match = providerFilterOptions.find(
      (opt) => opt.dnt_value === getResolvedProviderFilter()
    );
    return match ? match.title : '';
  };

  const getDistanceText = (): string => {
    const radiusLabel =
      radiusOptions.find((opt) => opt.dnt_value === radius)?.label || '';
    return location ? `${radiusLabel} of ${location}` : radiusLabel;
  };

  const getCoverageLabel = (): string =>
    COVERAGE_LABELS[coverageType] || 'medical';

  const formatHeading = (template: string): string =>
    template?.replace('{{ coverage }}', getCoverageLabel());

  const reviewHeading = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_HEADING
  );
  const reviewPrintOnlyHeading = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_PRINT_ONLY_HEADING
  );
  const reviewSubtitle = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_SUBTITLE
  );
  const reviewPrintOnlySubtitle = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_PRINT_ONLY_SUBTITLE
  );
  const popupBlockerWarning = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_POPUP_BLOCKER_WARNING
  );
  const popupBlockerPrintOnlyWarning = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION
      .PRINT_DIRECTORY_POPUP_BLOCKER_PRINT_ONLY_WARNING
  );
  const providerNetworkLabel = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_PROVIDER_NETWORK_LABEL
  );
  const directoryLabel = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_LABEL
  );
  const filterLabel = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_FILTER_LABEL
  );
  const distanceLabel = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_DISTANCE_LABEL
  );
  const printDirectoryButtonText = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_BUTTON_TEXT
  );
  const emailDirectoryButtonText = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.EMAIL_DIRECTORY_BUTTON_TEXT
  );
  // Note: Email-only messaging not in Lagoon, using heading/subtitle instead
  const sendEmailHeading = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_EMAIL_DIRECTORY_TITLE
  );
  const sendEmailSubtitle = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_EMAIL_ONLY_SUBTITLE
  );
  const locationLabel = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_LOCATION_LABEL
  );
  const searchRadiusLabel = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_SEARCH_RADIUS_LABEL
  );
  const searchRadiusPlaceholder = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION
      .PRINT_DIRECTORY_SEARCH_RADIUS_PLACEHOLDER
  );
  const generatingPdfTitle = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_GENERATING_PDF_TITLE
  );
  const generatingPdfMessage = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_GENERATING_PDF_MESSAGE
  );
  const pdfTitle = msg(
    ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_DIRECTORY_PDF_TITLE
  );

  const getReviewHeading = (): string => {
    if (isEmailSubmitted) return formatHeading(sendEmailHeading);
    const template = isDirectoryPrint ? reviewPrintOnlyHeading : reviewHeading;
    return formatHeading(template);
  };

  const getReviewSubtitle = (): string => {
    if (isEmailSubmitted) return sendEmailSubtitle;
    return isDirectoryPrint ? reviewPrintOnlySubtitle : reviewSubtitle;
  };

  const getPopupBlockerWarning = (): string =>
    isDirectoryPrint ? popupBlockerPrintOnlyWarning : popupBlockerWarning;

  const renderStepContent = () => {
    if (step === 1) {
      return renderRadioGroup(
        directoryTypeOptions,
        directoryType,
        (v) => {
          const nextDirectoryType = v as DirectoryType;
          setDirectoryType(nextDirectoryType);
          if (nextDirectoryType === 'places') {
            setProviderFilter('all');
          } else if (providerFilter === 'all') {
            setProviderFilter('');
          }
        },
        'Directory type selection',
        'print-directory-step-1'
      );
    }
    if (step === 2) {
      return renderRadioGroup(
        providerFilterOptions,
        providerFilter,
        (v) => setProviderFilter(v as ProviderFilter),
        'Provider filter selection',
        'print-directory-step-2'
      );
    }
    if (step === 3) {
      return (
        <LocationFormContainer data-testid="print-directory-step-3">
          <div>
            <label
              style={{
                display: 'block',
                fontWeight: 600,
                color: '#333333',
                fontSize: '14px',
                marginBottom: '4px',
                fontFamily: '"UHC Sans", Helvetica, Arial, sans-serif',
              }}
            >
              {locationLabel}
            </label>
            <div className="pd-location-wrapper">
              <LocationSearchDesktop
                findUserLocation={() => {}}
                location={location}
                onLocationSelect={(loc) => {
                  setSelectedLocation(loc);
                  setLocation(loc.label);
                }}
                setLocation={(value) => {
                  setLocation(value);
                  setSelectedLocation((prev) =>
                    prev?.label === value ? prev : { label: value }
                  );
                }}
              />
            </div>
          </div>
          <SelectInput
            css={{
              '.abyss-select-input-label': {
                fontWeight: 600,
                color: '#333333',
                fontSize: '14px',
              },
              '.abyss-select-input-input-container': {
                height: '40px',
                borderRadius: '8px',
                borderColor: '#E6E6E6',
              },
            }}
            data-testid="print-directory-radius-select"
            label={searchRadiusLabel}
            onChange={(val) => setRadius(String(val))}
            options={radiusOptions}
            placeholder={searchRadiusPlaceholder}
            value={radius}
          />
        </LocationFormContainer>
      );
    }

    // Step 4: Review Selections
    return (
      <div data-testid="print-directory-step-4">
        <ReviewSummaryList>
          <ReviewSummaryRow>
            <ReviewSummaryLabel data-testid="review-plan-label">
              {providerNetworkLabel}
            </ReviewSummaryLabel>
            <ReviewSummaryValue data-testid="review-plan-name">
              {selectedPlan?.planName}
            </ReviewSummaryValue>
          </ReviewSummaryRow>
          <ReviewSummaryRow>
            <ReviewSummaryLabel data-testid="review-directory-label">
              {directoryLabel}
            </ReviewSummaryLabel>
            <ReviewSummaryValue data-testid="review-directory-type">
              {getDirectoryTypeLabel()}
            </ReviewSummaryValue>
          </ReviewSummaryRow>
          {coverageType !== 'D' && (
            <ReviewSummaryRow>
              <ReviewSummaryLabel data-testid="review-filter-label">
                {filterLabel}
              </ReviewSummaryLabel>
              <ReviewSummaryValue data-testid="review-filter">
                {getFilterLabel()}
              </ReviewSummaryValue>
            </ReviewSummaryRow>
          )}
          <ReviewSummaryRow>
            <ReviewSummaryLabel>{distanceLabel}</ReviewSummaryLabel>
            <ReviewSummaryValue data-testid="review-distance">
              {getDistanceText()}
            </ReviewSummaryValue>
          </ReviewSummaryRow>
        </ReviewSummaryList>
        <ReviewWarningText data-testid="review-popup-warning">
          {getPopupBlockerWarning()}
        </ReviewWarningText>
      </div>
    );
  };

  const isDisabled = isContinueDisabled();

  return (
    <>
      <style>{SLIDE_KEYFRAMES}</style>
      <PageWrapper data-testid="print-directory-page" ref={pageRef}>
        <PageHeader data-testid="print-directory-header">
          <HeaderTitle>
            {msg(
              ConstantsLagoon.GUEST_PLAN_SELECTION.PRINT_EMAIL_DIRECTORY_TITLE
            )}
          </HeaderTitle>
          <HeaderMetaBlock>
            <HeaderMetaLabel>{providerNetworkLabel}</HeaderMetaLabel>
            <HeaderMetaValue>{selectedPlan?.planName || '—'}</HeaderMetaValue>
          </HeaderMetaBlock>
          <HeaderCloseButton
            aria-label="Close"
            data-testid="print-directory-close-button"
            onClick={() => navigate(-1)}
            type="button"
          >
            <IconMaterial css={{ color: '#002677' }} icon="close" size="32px" />
          </HeaderCloseButton>
        </PageHeader>

        <PageContentArea>
          <ProgressBarContainer data-testid="print-directory-step-indicator">
            {Array.from({ length: STEP_COUNT }).map((_, i) => (
              <ProgressSegment
                key={i}
                style={{ backgroundColor: i < step ? '#006DFF' : '#E6E6E6' }}
              />
            ))}
          </ProgressBarContainer>

          <ContentBlock
            className={
              slideDirection === 'right' ? 'pd-slide-right' : 'pd-slide-left'
            }
            key={animKey}
          >
            <StepTitle data-testid="print-directory-heading">
              {isEmailSubmitted
                ? formatHeading(sendEmailHeading)
                : step === 4
                ? getReviewHeading()
                : getStepHeading()}
            </StepTitle>
            <StepSubtitle data-testid="print-directory-subheading">
              {isEmailSubmitted ? (
                <span dangerouslySetInnerHTML={{ __html: sendEmailSubtitle }} />
              ) : step === 4 ? (
                getReviewSubtitle()
              ) : (
                getStepSubHeading()
              )}
            </StepSubtitle>

            <StyledDivider />
            <StepContentWrapper>{renderStepContent()}</StepContentWrapper>

            <ButtonGroup>
              {step === 4 ? (
                <>
                  <SecondaryActionButton
                    data-testid="print-directory-back-button"
                    onClick={handleBack}
                  >
                    {isEmailSubmitted ? t('Close') : t('Back')}
                  </SecondaryActionButton>
                  <PrimaryActionButton
                    data-testid="print-directory-print-button"
                    onClick={handlePrint}
                  >
                    <IconButtonInner>
                      <IconMaterial
                        css={{ color: '#FFFFFF' }}
                        icon="print"
                        size="18px"
                      />
                      <span>{printDirectoryButtonText}</span>
                    </IconButtonInner>
                  </PrimaryActionButton>
                  {!isDirectoryPrint && emailKey && (
                    <PrimaryActionButton
                      data-testid="print-directory-email-button"
                      onClick={() => {
                        setOpenSendEmailDrawer(true);
                      }}
                    >
                      <IconButtonInner>
                        <IconMaterial
                          css={{ color: '#FFFFFF' }}
                          icon="email"
                          size="18px"
                        />
                        <span>{emailDirectoryButtonText}</span>
                      </IconButtonInner>
                    </PrimaryActionButton>
                  )}
                </>
              ) : (
                <>
                  <SecondaryActionButton
                    data-testid="print-directory-cancel-back-button"
                    onClick={step === 1 ? () => navigate(-1) : handleBack}
                  >
                    {step === 1 ? t('Cancel') : t('Back')}
                  </SecondaryActionButton>
                  <PrimaryActionButton
                    data-testid="print-directory-continue-print-button"
                    disabled={isDisabled}
                    onClick={handleContinue}
                  >
                    {t('Continue')}
                  </PrimaryActionButton>
                </>
              )}
            </ButtonGroup>

            {openSendEmailDrawer ? (
              <Modal
                data-auto-testid="email-directory-drawer"
                data-testid="email-directory-drawer"
                isOpen={openSendEmailDrawer}
                onClose={() => {
                  setOpenSendEmailDrawer(false);
                }}
                title={emailDirectoryButtonText}
                titleAlign="center"
              >
                <Modal.Section>
                  <SendEmailForProviderList
                    handleSendEmail={handleSendEmail}
                    openShareContents={openSendEmailDrawer}
                    setOpenShareContents={setOpenSendEmailDrawer}
                  />
                </Modal.Section>
              </Modal>
            ) : null}
          </ContentBlock>
        </PageContentArea>
      </PageWrapper>

      {isPdfGenerating && (
        <PrintPdfGenerator
          generatingPdfMessage={generatingPdfMessage}
          generatingPdfTitle={generatingPdfTitle}
          isPdfGenerating={isPdfGenerating}
        />
      )}
    </>
  );
};
